import { ArrowBackOutlined, VerifiedOutlined } from '@mui/icons-material';
import { Alert, Divider, IconButton, Paper, Stack, Tooltip } from '@mui/material';
import { useEffect } from 'react';
import { DefinitionList, InformationPage, PageSections, ResourceState, SectionCard, StatusBadge } from '../../../design-system';
import { CertificateSealControl } from '../components/CertificateSealControl';
import { SettingRow } from '../components/SettingRow';
import type { TenantSettingsPageProps } from '../model/types';
import { useCompanyWorkspace } from '../model/useCompanyWorkspace';
import { useTenantSettingsRoute } from '../model/useTenantSettingsRoute';
export function CompanySealsPage({ port, route, fixtureMode, access }: TenantSettingsPageProps) {
  const location = useTenantSettingsRoute('admin-company-seals', route);
  const canRead = access.scopes.includes('certificates.settings.read');
  const canWrite = canRead && !access.readOnly && access.scopes.includes('certificates.settings.write');
  const profiles = useCompanyWorkspace(port, fixtureMode);
  const selected = profiles.companies.find((profile) => profile.companyId === location.companyId);
  const detail = location.view === 'company-seal';
  useEffect(() => {
    if (!detail || profiles.resource.loading || !profiles.ready || selected) return;
    route.navigate('admin-company-seals', 'company-seals', '', {
      replace: true,
    });
  }, [detail, profiles.ready, profiles.resource.loading, route, selected]);
  return (
    <InformationPage
      title={detail ? (selected ? `${selected.descriptor} 증명서 직인` : '증명서 직인') : '회사 직인'}
      help={detail ? '직인은 파일을 선택하고 확인하면 즉시 저장됩니다.' : undefined}
      titleRef={location.titleRef}
      width="compact"
      navigation={
        detail ? (
          <Tooltip title="회사 직인으로 돌아가기">
            <span>
              <IconButton aria-label="회사 직인으로 돌아가기" onClick={() => location.navigate('company-seals')}>
                <ArrowBackOutlined />
              </IconButton>
            </span>
          </Tooltip>
        ) : undefined
      }
    >
      {!canRead ? (
        <Alert severity="warning">회사 직인을 조회하려면 certificates.settings.read 권한이 필요합니다.</Alert>
      ) : detail ? (
        <PageSections>
          {!canWrite ? (
            <Alert severity="warning">직인 상태를 조회할 수 있지만 변경 권한은 없습니다.</Alert>
          ) : null}
          <ResourceState
            loading={profiles.resource.loading}
            error={profiles.resource.error?.message}
            empty={false}
            onRetry={profiles.resource.reload}
          >
            {selected ? (
              <SectionCard
                bodyPadding="standard"
                title={
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: 'center',
                    }}
                  >
                    <VerifiedOutlined color="action" fontSize="small" />
                    <span>증명서 직인</span>
                  </Stack>
                }
              >
                <Stack spacing={2}>
                  <DefinitionList
                    items={[
                      ['회사', selected.descriptor],
                      ['회사 코드', selected.companyCode],
                      ['현재 상태', <StatusBadge label={selected.sealConfigured ? '등록됨' : '등록되지 않음'} tone={selected.sealConfigured ? 'success' : 'warning'} />],
                    ]}
                  />
                  <Divider />
                  <CertificateSealControl
                    port={port}
                    companyId={selected.companyId}
                    companyLabel={selected.descriptor}
                    fixtureMode={fixtureMode}
                    readOnly={!canWrite}
                    disabled={!canWrite}
                    sealConfigured={selected.sealConfigured}
                    onConfiguredChange={(configured) => profiles.setSealConfigured(selected.companyId, configured)}
                  />
                </Stack>
              </SectionCard>
            ) : (
              <Alert severity="warning">선택한 법인 정보를 찾을 수 없습니다.</Alert>
            )}
          </ResourceState>
        </PageSections>
      ) : (
        <ResourceState
          loading={profiles.resource.loading}
          error={profiles.resource.error?.message}
          empty={profiles.ready && profiles.companies.length === 0}
          onRetry={profiles.resource.reload}
          loadingLabel="회사 직인 현황을 불러오는 중입니다."
          retryLabel="회사 직인 현황 다시 시도"
          emptyTitle="등록된 회사가 없습니다."
          emptyDescription="회사 원장에 회사를 등록한 뒤 직인을 설정해 주세요."
        >
          <Paper
            variant="outlined"
            sx={{
              overflow: 'hidden',
            }}
          >
            <Stack divider={<Divider />}>
              {profiles.companies.map((profile) => (
                <SettingRow
                  key={profile.companyId}
                  title={profile.descriptor}
                  value={profile.sealConfigured ? '직인 등록됨' : '직인 등록되지 않음'}
                  icon={<VerifiedOutlined fontSize="small" />}
                  statusLabel={profile.sealConfigured ? '사용 중' : '등록 필요'}
                  statusTone={profile.sealConfigured ? 'success' : 'warning'}
                  compact
                  ariaLabel={`${profile.descriptor} 증명서 직인 설정 열기`}
                  onClick={() => location.navigate('company-seal', profile.companyId)}
                />
              ))}
            </Stack>
          </Paper>
        </ResourceState>
      )}
    </InformationPage>
  );
}
